import React, { useState } from 'react';
import {
    Box,
    VStack,
    Heading,
    Text,
    Container,
    Button,
    Input,
    useColorModeValue,
    useBreakpointValue,
    Icon,
    useToast,
    InputGroup,
    InputRightElement,
    Spinner,
    Link as ChakraLink,
    Fade
} from '@chakra-ui/react';
import { FaGlobe, FaSearch, FaExternalLinkAlt, FaArrowLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';

const Login = () => {
    const API_BASE_URL = process.env.NODE_ENV === 'development'
        ? process.env.REACT_APP_API_BASE_URL_DEV
        : process.env.REACT_APP_API_BASE_URL_PROD;

    const [subdomain, setSubdomain] = useState('');
    const [isChecking, setIsChecking] = useState(false);
    const [domainExists, setDomainExists] = useState(null);
    const [sanitizedDomain, setSanitizedDomain] = useState('');
    const toast = useToast();

    const textColor = useColorModeValue('gray.800', 'gray.100');
    const accentColor = useColorModeValue('yellow.400', 'yellow.300');
    const cardBgColor = useColorModeValue('white', 'gray.700');

    const headingSize = useBreakpointValue({ base: 'xl', md: '2xl' });
    const iconSize = useBreakpointValue({ base: '10', md: '12' });
    const buttonSize = useBreakpointValue({ base: 'md', md: 'lg' });

    const sanitizeSubdomain = (name) => {
        return name
            .toLowerCase()
            .replace(/@/g, '-at-')
            .replace(/&/g, '-and-')
            .replace(/[^a-z0-9-]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .substring(0, 63);
    };

    const checkSubdomainExists = async () => {
        if (!subdomain.trim()) {
            toast({
                title: 'Please enter your subdomain',
                status: 'warning',
                duration: 3000,
                isClosable: true,
                position: 'top'
            });
            return;
        }

        setIsChecking(true);
        const sanitized = sanitizeSubdomain(subdomain);
        setSanitizedDomain(sanitized);

        try {
            // Make API call to backend to check subdomain availability
            const response = await axios.post(
                `${API_BASE_URL}/api/landingAuth/check-subdomain-availability`,
                { subdomain: sanitized }
            );

            // If response is 200, subdomain is available (doesn't exist)
            if (response.status === 200) {
                setDomainExists(false);
                toast({
                    title: 'Subdomain not found',
                    description: `${sanitized}.piepos.in doesn't exist. Please check your subdomain or register first.`,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                    position: 'top'
                });
            }
        } catch (error) {
            if (error.response && error.response.status === 400) {
                // Subdomain is already taken (exists!)
                setDomainExists(true);
                toast({
                    title: 'Subdomain found!',
                    description: `Redirecting to ${sanitized}.piepos.in`,
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                    position: 'top'
                });
            } else {
                // Server error or other issues
                setDomainExists(false);
                toast({
                    title: 'Error checking subdomain',
                    description: 'Please try again later',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                    position: 'top'
                });
                console.error('Error checking subdomain:', error);
            }
        } finally {
            setIsChecking(false);
        }
    };

    const handleInputChange = (e) => {
        setSubdomain(e.target.value);
        // Reset status when user types
        if (domainExists !== null) {
            setDomainExists(null);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            checkSubdomainExists();
        }
    };

    return (
        <Box minH="100vh" position="relative" overflow="hidden">
            {/* Back to Home Button */}
            <Box
                position="absolute"
                top={{ base: 4, md: 6 }}
                left={{ base: 4, md: 6 }}
                zIndex="10"
            >
                <Button
                    as={Link}
                    to="/"
                    variant="ghost"
                    leftIcon={<FaArrowLeft />}
                    size={{ base: "sm", md: "md" }}
                    color={textColor}
                    _hover={{ bg: 'blackAlpha.100', transform: 'translateX(-2px)' }}
                    transition="all 0.2s"
                    fontWeight="medium"
                >
                    Home
                </Button>
            </Box>
            {/* Mobile decorations */}
            <Box
                display={{ base: 'block', md: 'none' }}
                position="absolute"
                top="8%"
                right="5%"
                w={{ base: '40px', sm: '50px' }}
                h={{ base: '40px', sm: '50px' }}
                bg={accentColor}
                borderRadius="50%"
                opacity="0.6"
                zIndex="0"
                animation="bounce 3s ease-in-out infinite"
                sx={{
                    '@keyframes bounce': {
                        '0%, 100%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(-10px)' }
                    }
                }}
            />

            <Box
                display={{ base: 'block', md: 'none' }}
                position="absolute"
                bottom="10%"
                left="5%"
                w={{ base: '30px', sm: '35px' }}
                h={{ base: '30px', sm: '35px' }}
                bg="blue.200"
                borderRadius="50% 50% 0 50%"
                opacity="0.7"
                zIndex="0"
                animation="wiggle 4s ease-in-out infinite"
                sx={{
                    '@keyframes wiggle': {
                        '0%, 100%': { transform: 'rotate(0deg)' },
                        '50%': { transform: 'rotate(15deg)' }
                    }
                }}
            />

            {/* Desktop decorations */}
            <Box
                display={{ base: 'none', md: 'block' }}
                position="absolute"
                top="15%"
                left="10%"
                w="150px"
                h="150px"
                bg={accentColor}
                borderRadius="50% 40% 60% 30%"
                opacity="0.5"
                zIndex="0"
            />

            <Box
                display={{ base: 'none', md: 'block' }}
                position="absolute"
                bottom="20%"
                right="15%"
                w="120px"
                h="120px"
                bg="blue.200"
                borderRadius="40% 60% 30% 70%"
                opacity="0.6"
                zIndex="0"
            />

            {/* Gradient Overlays */}
            <Container
                maxW="7xl"
                position="relative"
                zIndex="1"
                minH="100vh"
                display="flex"
                alignItems="center"
                justifyContent="center"
                py={{ base: 8, sm: 10, md: 0 }}
                px={{ base: 3, sm: 4, md: 6 }}
            >
                <VStack spacing={{ base: 6, sm: 8, md: 12 }} w="full" maxW="2xl">

                    {/* Header Section */}
                    <VStack spacing={{ base: 3, sm: 4, md: 6 }} textAlign="center" w="full" px={{ base: 2, sm: 0 }}>
                        <Heading
                            as="h2"
                            size={headingSize}
                            color={textColor}
                            fontWeight="bold"
                            lineHeight="1.2"
                            mt={10}
                        >
                            Login to Your Account
                        </Heading>

                        <Box position="relative" w="full">
                            <Text
                                fontSize={{ base: 'sm', sm: 'md', md: 'lg' }}
                                color="gray.600"
                                fontWeight="medium"
                                lineHeight="1.6"
                                px={{ base: 2, sm: 0 }}
                            >
                                Enter your{' '}
                                <Box as="span" position="relative" display="inline-block">
                                    restaurant subdomain
                                    {/* Doodle underline */}
                                    <Box
                                        position="absolute"
                                        bottom={-1}
                                        left="0"
                                        w="100%"
                                        h={{ base: '2px', md: '3px' }}
                                        bg={accentColor}
                                        borderRadius="full"
                                        transform="rotate(-1deg)"
                                        opacity={0.8}
                                    />
                                </Box>
                                {' '}to access your PiePOS dashboard.
                            </Text>
                        </Box>
                    </VStack>

                    {/* Login Card */}
                    <Box
                        bg={cardBgColor}
                        p={{ base: 4, sm: 6, md: 8 }}
                        borderRadius={{ base: 'xl', md: '2xl' }}
                        border="2px solid"
                        borderColor={accentColor}
                        boxShadow="xl"
                        w="full"
                        position="relative"
                        overflow="hidden"
                    >
                        {/* Card decoration */}
                        <Box
                            position="absolute"
                            top="-40px"
                            right="-40px"
                            w={{ base: '80px', md: '120px' }}
                            h={{ base: '80px', md: '120px' }}
                            bg="yellow.50"
                            borderRadius="50%"
                            opacity="0.5"
                            zIndex="0"
                        />

                        <VStack spacing={{ base: 4, sm: 5, md: 6 }} position="relative" zIndex="1">
                            <VStack spacing={{ base: 1, sm: 2 }}>
                                <Icon as={FaGlobe} boxSize={iconSize} color={accentColor} />
                                <Text
                                    fontSize={{ base: 'md', sm: 'lg' }}
                                    color={textColor}
                                    fontWeight="bold"
                                >
                                    Find Your Restaurant
                                </Text>
                                <Text
                                    fontSize={{ base: 'xs', sm: 'sm' }}
                                    color="gray.500"
                                    textAlign="center"
                                    px={{ base: 2, sm: 0 }}
                                >
                                    Enter your restaurant's subdomain to continue
                                </Text>
                            </VStack>

                            <VStack spacing={{ base: 3, md: 4 }} w="full">
                                <VStack w="full" spacing={2}>
                                    <InputGroup flex="1" w="full">
                                        <Input
                                            placeholder="yourrestaurant"
                                            value={subdomain}
                                            onChange={handleInputChange}
                                            onKeyPress={handleKeyPress}
                                            bg="gray.50"
                                            borderColor="gray.300"
                                            focusBorderColor={accentColor}
                                            size={buttonSize}
                                            borderRadius="lg"
                                            _placeholder={{ color: 'gray.400' }}
                                            fontSize={{ base: 'sm', md: 'md' }}
                                        />
                                        {isChecking && (
                                            <InputRightElement height="100%">
                                                <Spinner size="sm" color={accentColor} />
                                            </InputRightElement>
                                        )}
                                    </InputGroup>
                                    <Text
                                        fontSize={{ base: 'sm', sm: 'md', md: 'lg' }}
                                        color="gray.600"
                                        fontWeight="medium"
                                        alignSelf="center"
                                        pl={1}
                                    >
                                        .piepos.in
                                    </Text>
                                </VStack>

                                <Button
                                    size={buttonSize}
                                    bg={accentColor}
                                    color="gray.900"
                                    _hover={{ bg: 'yellow.500', transform: 'translateY(-2px)' }}
                                    _active={{ bg: 'yellow.600' }}
                                    width="full"
                                    borderRadius="lg"
                                    leftIcon={<FaSearch />}
                                    onClick={checkSubdomainExists}
                                    isLoading={isChecking}
                                    loadingText="Searching..."
                                    transition="all 0.3s ease"
                                    fontWeight="bold"
                                    fontSize={{ base: 'sm', md: 'md' }}
                                >
                                    Find My Restaurant
                                </Button>
                            </VStack>

                            {/* Domain Found Display */}
                            {domainExists === true && (
                                <Fade in={domainExists === true}>
                                    <Box
                                        bg="green.50"
                                        p={{ base: 4, sm: 5, md: 6 }}
                                        borderRadius="xl"
                                        border="2px solid"
                                        borderColor="green.200"
                                        w="full"
                                    >
                                        <VStack spacing={{ base: 3, md: 4 }}>
                                            <VStack spacing={2}>
                                                <Icon
                                                    as={FaGlobe}
                                                    color="green.500"
                                                    boxSize={{ base: '6', sm: '7', md: '8' }}
                                                />
                                                <Text
                                                    fontSize={{ base: 'md', md: 'lg' }}
                                                    fontWeight="bold"
                                                    color="green.700"
                                                >
                                                    Restaurant Found!
                                                </Text>
                                                <Text
                                                    fontSize={{ base: 'xs', sm: 'sm' }}
                                                    color="green.600"
                                                    textAlign="center"
                                                    px={{ base: 2, sm: 0 }}
                                                >
                                                    You can login to your account using this link:
                                                </Text>
                                            </VStack>

                                            <ChakraLink
                                                href={`https://${sanitizedDomain}.piepos.in`}
                                                isExternal
                                                w="full"
                                            >
                                                <Button
                                                    size={buttonSize}
                                                    bg="green.500"
                                                    color="white"
                                                    _hover={{ bg: 'green.600', transform: 'translateY(-3px)' }}
                                                    _active={{ bg: 'green.700' }}
                                                    rightIcon={<FaExternalLinkAlt />}
                                                    borderRadius="lg"
                                                    boxShadow="lg"
                                                    transition="all 0.3s ease"
                                                    w="full"
                                                    animation="glow 2s ease-in-out infinite alternate"
                                                    fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
                                                    sx={{
                                                        '@keyframes glow': {
                                                            '0%': { boxShadow: '0 0 5px rgba(34, 197, 94, 0.5)' },
                                                            '100%': { boxShadow: '0 0 20px rgba(34, 197, 94, 0.8)' }
                                                        }
                                                    }}
                                                >
                                                    {sanitizedDomain}.piepos.in
                                                </Button>
                                            </ChakraLink>
                                        </VStack>
                                    </Box>
                                </Fade>
                            )}

                            {/* Domain Not Found Display */}
                            {domainExists === false && (
                                <Fade in={domainExists === false}>
                                    <Box
                                        bg="red.50"
                                        p={{ base: 3, sm: 4 }}
                                        borderRadius="xl"
                                        border="2px solid"
                                        borderColor="red.200"
                                        w="full"
                                    >
                                        <VStack spacing={{ base: 2, md: 3 }}>
                                            <Text
                                                fontSize={{ base: 'sm', md: 'md' }}
                                                fontWeight="bold"
                                                color="red.700"
                                            >
                                                Subdomain Not Found
                                            </Text>
                                            <Text
                                                fontSize={{ base: 'xs', sm: 'sm' }}
                                                color="red.600"
                                                textAlign="center"
                                                px={{ base: 2, sm: 0 }}
                                            >
                                                This subdomain doesn't exist. Please check your spelling or register a new account.
                                            </Text>
                                            <Button
                                                as={Link}
                                                to="/register"
                                                size="sm"
                                                variant="outline"
                                                colorScheme="red"
                                                borderRadius="lg"
                                                mt={2}
                                                fontSize={{ base: 'xs', sm: 'sm' }}
                                            >
                                                Create New Account
                                            </Button>
                                        </VStack>
                                    </Box>
                                </Fade>
                            )}
                        </VStack>
                    </Box>

                    {/* Help Section */}
                    <Box
                        textAlign="center"
                        w="full"
                        px={{ base: 2, sm: 0 }}
                        pb={{ base: 6, md: 0 }}
                    >
                        <Text fontSize={{ base: 'xs', sm: 'sm' }} color="gray.500">
                            Don't have an account yet?{' '}
                            <ChakraLink
                                as={Link}
                                to="/register"
                                color="blue.500"
                                fontWeight="bold"
                                _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                            >
                                Register here
                            </ChakraLink>
                        </Text>

                        <Text fontSize={{ base: 'xs', sm: 'sm' }} color="gray.500" mt={2}>
                            Want a QR code for your restaurant?{' '}
                            <ChakraLink
                                as={Link}
                                to="/generateQR"
                                color="blue.500"
                                fontWeight="bold"
                                _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                            >
                                Generate your custom QR
                            </ChakraLink>
                        </Text>

                        <Text fontSize="xs" color="gray.500" mb={10}>
                            Need help?{' '}
                            <Button
                                as="a"
                                href="mailto:sankalp@piepos.in?subject=Help with Login"
                                variant="link"
                                color="blue.500"
                                fontWeight="bold"
                                fontSize="xs"
                                _hover={{ color: 'blue.600', textDecoration: 'underline' }}
                            >
                                Contact us
                            </Button>
                        </Text>
                    </Box>

                </VStack>
            </Container>
        </Box>
    );
};

export default Login;